import React, { useContext, useState } from 'react';
import dayjs from 'dayjs';

import {
  OfficesContext,
  ProfileContext,
} from '../../../context/ProfileContext';
import {
  ListGroup,
  ListGroupItem,
  Button,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Badge,
} from 'reactstrap';
import OfficeAddEditModal from './OfficeAddEditModal';
import agent from '../../../agent';
import { OfficesContextType } from '../../../context/ProfileContext';
import OfficePhotos from './OfficePhotos';
import { FaTrash } from 'react-icons/fa';
import OfficeHoursAddEditModal from './OfficeHoursAddEditModal';
import { Link } from 'gatsby';
import { OfficeProfileViewType } from '../../../types/services-api';
import OutboundLink from '../../common/OutboundLink';

const customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);

const OfficesList = (props: { publicList?: OfficeProfileViewType[] }) => {
  const { offices, removeOffice, updatePrimaryOffice } = useContext(
    OfficesContext
  ) as OfficesContextType;
  const { profile } = useContext(ProfileContext);
  const [officeToDelete, setOfficeToDelete] =
    useState<OfficeProfileViewType | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  function confirmedDeleteOffice() {
    if (!!officeToDelete) {
      agent.Employer.deleteOffice({ officeId: officeToDelete.id })
        .then((res) => {
          removeOffice(officeToDelete.id);
          setOfficeToDelete(null);
        })
        .catch((err) => {
          setErrorMessage(err.message);
        });
    }
  }

  const officeList = !!props.publicList ? props.publicList : offices;
  return (
    <ListGroup>
      {officeList.map((office, index) => {
        return (
          <ListGroupItem
            key={office.id}
            className="mb-3 border-top border-light rounded"
          >
            <Container fluid className="px-3 pb-3">
              <Row>
                {!props.publicList && (
                  <>
                    {index === 0 && (
                      <Col xs="auto" className="d-flex align-items-center">
                        <Badge color="secondary">PRIMARY OFFICE</Badge>
                      </Col>
                    )}

                    <Col xs="auto" className="p-0 ms-auto">
                      <OfficeAddEditModal office={office} />
                      {index > 0 && (
                        <Button
                          color="danger"
                          outline
                          className="ms-3"
                          onClick={() => setOfficeToDelete(office)}
                        >
                          <FaTrash className="text-danger" />
                        </Button>
                      )}
                    </Col>
                  </>
                )}
              </Row>
              <Row className="d-flex flex-wrap">
                <Col
                  xs="auto"
                  className={`mb-4 ${!!props.publicList ? 'col-12 mt-4' : ''}`}
                >
                  <div style={{ width: '300px', maxWidth: '100%' }}>
                    <h5>{office.name}</h5>
                    {office.address && (
                      <span className="d-block">
                        {office.address} {office.address2}
                      </span>
                    )}
                    <p className="mb-1">
                      {office.city}, {office.state} {office.zip}
                    </p>
                    {!!office.website && (
                      <OutboundLink
                        href={
                          office.website.startsWith('http')
                            ? office.website
                            : `https://${office.website}`
                        }
                      >
                        {office.website}
                      </OutboundLink>
                    )}
                    {index > 0 && !props.publicList && (
                      <Link
                        to="#"
                        onClick={() => updatePrimaryOffice(office.id)}
                      >
                        Set as Primary Office
                      </Link>
                    )}
                  </div>
                </Col>
                <hr />
                <Col xs="12" lg="8" className="mb-4">
                  <p className="mb-0 text-secondary">
                    {!props.publicList
                      ? 'ABOUT YOUR OFFICE'
                      : 'ABOUT THIS OFFICE'}
                  </p>
                  <p>{office.about}</p>
                </Col>
                <Col xs="auto" className="mb-4">
                  <div style={{ width: '300px', maxWidth: '100%' }}>
                    <p className="mb-0 text-secondary">Office Hours</p>
                    {office.hours.map((day) => {
                      return (
                        <p
                          key={`office-${office.id}-${day.day}`}
                          className="d-flex align-items-center"
                        >
                          <span className="fw-bold text-muted me-2">
                            {dayjs().day(day.day).format('dddd')}:{' '}
                          </span>
                          {dayjs(day.openTime, 'HH:mm').format('h:mma')} -{' '}
                          {dayjs(day.closeTime, 'HH:mm').format('h:mma')}
                        </p>
                      );
                    })}
                    {office.hours.length === 0 &&
                      !!props.publicList &&
                      'None Provided'}
                    {!props.publicList && (
                      <OfficeHoursAddEditModal office={office} />
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <p className="text-secondary">
                    OFFICE PHOTOS{' '}
                    {!props.publicList && (
                      <>
                        <br />
                        <small className="text-black">
                          Add photos to showcase your office to jobseekers. Drag
                          & drop photos to rearrange them.
                        </small>
                      </>
                    )}
                  </p>
                  <OfficePhotos office={office} />
                  {office.images.length === 0 &&
                    !!props.publicList &&
                    'None Provided'}
                </Col>
              </Row>
            </Container>
          </ListGroupItem>
        );
      })}
      <Modal isOpen={!!officeToDelete} toggle={() => setOfficeToDelete(null)}>
        <ModalHeader>Warning</ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete the office '{officeToDelete?.name}
            '?
          </p>
          <p>This action cannot be undone.</p>
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <Button onClick={() => setOfficeToDelete(null)} color="link">
              Cancel
            </Button>
            <Button onClick={confirmedDeleteOffice} color="primary">
              Delete
            </Button>
          </div>
          <Alert isOpen={!!errorMessage} color="danger" className="w-100 mt-3">
            {errorMessage}
          </Alert>
        </ModalFooter>
      </Modal>
    </ListGroup>
  );
};
export default OfficesList;
